// The Assistant's transport seam (TASK-664): how the library screens reach a session, whichever
// shell mounts them. The staff implementation is API-310 and API-311 through the shell's ApiClient
// (the admin BFF's session cookie and CSRF token), polled with `?after=<seq>`. The proxy's
// (TASK-677) reads sessions, history and changes the same way over nexus-web's forwarder, but a
// turn is API-586: one POST whose answer is the turn's event stream, because the customer loop
// runs inside that request (ADR-064). The session's proposed changes (API-312, TASK-671) travel
// the same seam: list, read one, approve, reject.
import { createContext } from "preact";
import { useContext } from "preact/hooks";
import type { ApiClient } from "../../api/client";
import { ApiError, toApiError } from "../../api/errors";
import type { AgentChange, ApproveAgentChangeRequest } from "./confirmations";
import type { AgentMessage, AgentMessageList, AgentSession } from "./model";

/** A tool call's outcome as API-586 reports it. */
export type TurnOutcome = "ok" | "rejected" | "error";

/**
 * One API-586 event (`web/server/internal/assistant/ports.go`), by its `event:` name. `message`,
 * `tool` and `change` arrive as the turn runs, each with its transcript row's `seq`; `done` or
 * `error` is always last.
 */
export type TurnEvent =
  | { event: "message"; seq: number; text: string }
  | { event: "tool"; seq: number; name: string; outcome: TurnOutcome }
  | { event: "change"; seq: number; change: AgentChange }
  | { event: "done"; stopReason: "end" | "tool_limit"; toolCalls: number }
  | { event: "error"; code: string; message: string };

/** How a streamed turn ended: its last event. */
export type TurnEnd = Extract<TurnEvent, { event: "done" | "error" }>;

/** The code of a stream that closed before its `done` or `error` (a dropped connection). */
export const TURN_INTERRUPTED = "interrupted";

export interface AssistantTransport {
  /** The org's sessions, newest first, one page. */
  list(): Promise<AgentSession[]>;
  /** A new session; the API answers 404 `agent_disabled` or 429 `agent_daily_budget_exhausted`. */
  create(): Promise<AgentSession>;
  get(sessionId: string): Promise<AgentSession>;
  /** The operator's message; it starts a turn. 409 `session_terminal` once the session ended. */
  send(sessionId: string, text: string): Promise<AgentMessage>;
  /** The rows after `after` (0 is the whole transcript) and the session's state. */
  poll(sessionId: string, after: number): Promise<AgentMessageList>;
  end(sessionId: string): Promise<AgentSession>;
  /** The session's changes, newest proposal first, one page; a proposal past its hour reads `expired`. */
  changes(sessionId: string): Promise<AgentChange[]>;
  change(sessionId: string, changeId: string): Promise<AgentChange>;
  /**
   * Approve with the digest as shown and the phrases the person typed: 409 `artifact_changed`,
   * `change_expired`, `change_not_proposed` or `target_changed`; 422 `confirmation_required`; 403
   * the operation's own refusal. Answers the change as applied (with its read-back) or failed.
   */
  approve(sessionId: string, changeId: string, request: ApproveAgentChangeRequest): Promise<AgentChange>;
  /** Reject with a reason (1–500 characters) the Assistant sees; 409 `change_not_proposed`. */
  reject(sessionId: string, changeId: string, reason: string): Promise<AgentChange>;
  /**
   * A transport whose turns stream (the proxy's API-586) has this, and the conversation uses it
   * instead of `send`: the operator's text starts the turn, each event is handed to `onEvent` as
   * it arrives, and the answer is the last event. A refusal before the stream (409
   * `assistant_model_unavailable`, 429 `quota_exceeded`, 503 `busy` or `api_unreachable`, the
   * API's 404 `agent_disabled`, 409 `awaiting_operator` or `turn_tool_limit`) rejects with its
   * ApiError. Such a refusal may come after the message was posted (a failed read of the tools or
   * the history, the first model call or the first step): the message is then recorded and the
   * session may still read `active`. So the caller reads the history again after every turn,
   * refused or not, and treats `active` with no stream running as idle.
   */
  turn?(sessionId: string, text: string, onEvent: (event: TurnEvent) => void): Promise<TurnEnd>;
}

/** API-310/311 for one org through the shell's client. */
export function staffTransport(client: ApiClient, orgId: string): AssistantTransport {
  const session = (sessionId: string) => ({ path: { orgId, sessionId } });
  const change = (sessionId: string, changeId: string) => ({ path: { orgId, sessionId, changeId } });
  return {
    list: () => client.get("/v1/orgs/{orgId}/agent/sessions", { path: { orgId } }).then((page) => page.items),
    create: () => client.post("/v1/orgs/{orgId}/agent/sessions", { path: { orgId }, body: {} }),
    get: (sessionId) => client.get("/v1/orgs/{orgId}/agent/sessions/{sessionId}", session(sessionId)),
    send: (sessionId, text) => client.post("/v1/orgs/{orgId}/agent/sessions/{sessionId}/messages", { ...session(sessionId), body: { text } }),
    poll: (sessionId, after) => client.get("/v1/orgs/{orgId}/agent/sessions/{sessionId}/messages", { ...session(sessionId), query: { after } }),
    end: (sessionId) => client.post("/v1/orgs/{orgId}/agent/sessions/{sessionId}/end", session(sessionId)),
    changes: (sessionId) => client.get("/v1/orgs/{orgId}/agent/sessions/{sessionId}/changes", session(sessionId)).then((page) => page.items),
    change: (sessionId, changeId) => client.get("/v1/orgs/{orgId}/agent/sessions/{sessionId}/changes/{changeId}", change(sessionId, changeId)),
    approve: (sessionId, changeId, request) =>
      client.post("/v1/orgs/{orgId}/agent/sessions/{sessionId}/changes/{changeId}/approve", { ...change(sessionId, changeId), body: request }).then((answer) => answer.change),
    reject: (sessionId, changeId, reason) => client.post("/v1/orgs/{orgId}/agent/sessions/{sessionId}/changes/{changeId}/reject", { ...change(sessionId, changeId), body: { reason } }),
  };
}

/** The refusals a streamed turn adds to the API's (REQ-118: each has a Help topic). */
export type TurnRefusalCode =
  | "assistant_model_unavailable"
  | "quota_exceeded"
  | "busy"
  | "api_unreachable"
  | "awaiting_operator"
  | "turn_tool_limit"
  | "turn_ceiling"
  | "policy_refused"
  | "upstream"
  | typeof TURN_INTERRUPTED;

export interface TurnRefusal {
  code: TurnRefusalCode;
  title: string;
  text: string;
  /** The Help topic id each shell holds. */
  help: string;
}

export const TURN_REFUSALS: Readonly<Record<TurnRefusalCode, Omit<TurnRefusal, "code">>> = {
  assistant_model_unavailable: {
    title: "The Assistant's model is not available to you",
    text: "The model the Assistant runs on is not among the models your sign-in may use.",
    help: "assistant.modelUnavailable",
  },
  quota_exceeded: {
    title: "Your model usage is over its quota",
    text: "The Assistant runs on your own model usage on this proxy, and a quota has stopped it for now.",
    help: "assistant.quotaExceeded",
  },
  busy: { title: "The Assistant is busy", text: "Too many answers are running on this proxy. Try again in a moment.", help: "assistant.busy" },
  api_unreachable: {
    title: "AllCode's API is unreachable",
    text: "The Assistant reads and changes settings through AllCode's API, which this proxy cannot reach right now.",
    help: "assistant.apiUnreachable",
  },
  awaiting_operator: {
    title: "The conversation is paused",
    text: "It had not heard from you for a while. Send a message to continue.",
    help: "assistant.awaitingOperator",
  },
  turn_tool_limit: {
    title: "The Assistant stopped after eight steps",
    text: "One answer may read or propose at most eight times. Ask again to carry on from where it stopped.",
    help: "assistant.turnToolLimit",
  },
  turn_ceiling: {
    title: "The answer took too long",
    text: "One answer may run for a few minutes at most, and this one ran past that. What it did so far is below; ask again to carry on.",
    help: "assistant.turnCeiling",
  },
  policy_refused: {
    title: "This proxy refused the model call",
    text: "The proxy's own rules declined the Assistant's model call for your sign-in, as they would a chat message.",
    help: "assistant.policyRefused",
  },
  upstream: {
    title: "The model could not be reached",
    text: "The model the Assistant runs on did not answer. What it did so far is below; try again in a moment.",
    help: "assistant.upstream",
  },
  interrupted: {
    title: "The answer was cut off",
    text: "The connection closed before the Assistant finished. What it did so far is below.",
    help: "assistant.interrupted",
  },
};

export const TURN_HELP_IDS: readonly string[] = Object.values(TURN_REFUSALS).map((r) => r.help);

/** The turn refusal an error is, with its words and Help topic; null for any other error. */
export function turnRefusalFrom(error: ApiError): TurnRefusal | null {
  const code = error.code as TurnRefusalCode;
  return code in TURN_REFUSALS ? { code, ...TURN_REFUSALS[code] } : null;
}

/** Where nexus-web serves API-586 (same-origin; CSRF-checked like the proxy's other admin posts). */
export const ASSISTANT_TURNS_PATH = "/api/assistant/turns";

/** A same-origin fetch that carries the session's cookie and CSRF header (the proxy shell's). */
export type SessionFetch = (path: string, init: RequestInit) => Promise<Response>;

/**
 * The proxy's transport (TASK-677): sessions, history and changes are API-310/311/312 through the
 * shell's client, mounted on nexus-web's forwarder (`/api/admin/v1/orgs/…`), so it is the staff
 * transport's reads and decisions over another client; a turn is API-586 through `post`. There is
 * no bare `send` on the proxy: a message no loop answers would leave the session waiting, so it
 * refuses and the conversation uses `turn`.
 */
export function proxyTransport(client: ApiClient, orgId: string, post: SessionFetch): AssistantTransport {
  return {
    ...staffTransport(client, orgId),
    send: () => Promise.reject(new ApiError(0, "internal", "This proxy's Assistant takes messages only as turns.")),
    turn: (sessionId, text, onEvent) => streamTurn(post, sessionId, text, onEvent),
  };
}

/** A non-2xx answer as an ApiError: the API's envelope, or nexus-web's own `{code, message}`. */
async function refusal(response: Response): Promise<ApiError> {
  const body: unknown = await response.json().catch(() => null);
  const retryAfter = response.headers.get("Retry-After");
  const flat = body as { code?: unknown; message?: unknown } | null;
  if (flat && typeof flat.code === "string" && typeof flat.message === "string") {
    return toApiError(response.status, { error: { code: flat.code, message: flat.message } }, retryAfter);
  }
  return toApiError(response.status, body, retryAfter);
}

/** POST API-586 and read its event stream to the end (fetch and a stream reader: EventSource cannot POST). */
export async function streamTurn(post: SessionFetch, sessionId: string, text: string, onEvent: (event: TurnEvent) => void): Promise<TurnEnd> {
  const response = await post(ASSISTANT_TURNS_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify({ sessionId, text }),
  });
  if (!response.ok) throw await refusal(response);
  let end: TurnEnd | null = null;
  if (response.body) {
    await readEvents(response.body, (event) => {
      if (end !== null) return;
      if (event.event === "done" || event.event === "error") end = event;
      onEvent(event);
    });
  }
  if (end !== null) return end;
  const cut: TurnEnd = { event: "error", code: TURN_INTERRUPTED, message: "The connection closed before the Assistant finished." };
  onEvent(cut);
  return cut;
}

/**
 * Read a `text/event-stream` body: each block of `event:` and `data:` lines, separated by a blank
 * line, becomes one TurnEvent; comments, unknown names and data that is not JSON are skipped.
 */
export async function readEvents(body: ReadableStream<Uint8Array>, onEvent: (event: TurnEvent) => void): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffered = "";
  for (;;) {
    const { done, value } = await reader.read();
    buffered += done ? decoder.decode() : decoder.decode(value, { stream: true });
    const blocks = buffered.split(/\r?\n\r?\n/);
    buffered = done ? "" : (blocks.pop() ?? "");
    for (const block of blocks) {
      const event = parseEvent(block);
      if (event) onEvent(event);
    }
    if (done) return;
  }
}

const EVENT_NAMES: ReadonlySet<string> = new Set(["message", "tool", "change", "done", "error"]);

/** One SSE block as a TurnEvent, or null. */
export function parseEvent(block: string): TurnEvent | null {
  let name = "message";
  const data: string[] = [];
  for (const line of block.split(/\r?\n/)) {
    if (line.startsWith("event:")) name = line.slice(6).trim();
    else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
  }
  if (!EVENT_NAMES.has(name) || data.length === 0) return null;
  try {
    const payload: unknown = JSON.parse(data.join("\n"));
    if (typeof payload !== "object" || payload === null || Array.isArray(payload)) return null;
    return { ...(payload as object), event: name } as TurnEvent;
  } catch {
    return null;
  }
}

/** Provided by the shell around the Assistant screens; there is no default transport. */
export const AssistantTransportContext = createContext<AssistantTransport | null>(null);

export function useAssistantTransport(): AssistantTransport {
  const transport = useContext(AssistantTransportContext);
  if (!transport) throw new Error("useAssistantTransport: no AssistantTransportContext provider");
  return transport;
}
